import { useMemo, useState } from "react";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentQuestionComment,
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
  AssessmentType,
  ComplianceProgramStep,
  OrganizationTalentIndicatorFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import InfoTooltip from "@components/tooltip/info-tooltip";
import WarningTooltip from "@components/tooltip/warning-tooltip";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";

import AssessmentGroupSidebar from "../assessment-group-sidebar/assessment-group-sidebar";
import TalentIndicatorSidebar from "../talent-indicator-sidebar/talent-indicator-sidebar";
import { ProgramForm, ProgramFormStep } from "../types";
import ComplianceProgramFormStep from "./compliance-program-form-step";
import QuestionSetSelector from "./question-set-selector";

const UpwardAssessmentStep = ({
  proposedComplianceProgram,
  canUpdate,
  talentIndicatorData,
  onAddOrUpdateStep,
  availableAssessmentGroups,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
}: {
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  talentIndicatorData?: OrganizationTalentIndicatorFragment[];
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
}) => {
  const label = useLabel();

  const [isAssessmentGroupSidebarOpen, setIsAssessmentGroupSidebarOpen] =
    useState(false);
  const [isTalentIndicatorSidebarOpen, setIsTalentIndicatorSidebarOpen] =
    useState(false);
  const [assessmentGroupToDuplicate, setAssessmentGroupToDuplicate] =
    useState<AssessmentGroupListFragmentFragment | null>(null);

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(({ assessmentType }) => assessmentType === AssessmentType.Manager)
      .filter((assessmentGroup) => !assessmentGroup.hasNpsQuestions)
      .map(({ id, title }) => ({
        value: id,
        label: title,
      }));
  }, [availableAssessmentGroups]);

  const upwardReviewStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.UpwardReview,
    );
  }, [proposedComplianceProgram]);

  const selectedAssessmentGroup = useMemo(() => {
    return upwardReviewStep && upwardReviewStep.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) => id === upwardReviewStep.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, upwardReviewStep]);

  const applicableTalentIndicators = useMemo(() => {
    return talentIndicatorData
      ? talentIndicatorData.filter(
          (talentIndicator) => talentIndicator.isManagerIndicator,
        )
      : [];
  }, [talentIndicatorData]);

  return (
    upwardReviewStep && (
      <>
        {isTalentIndicatorSidebarOpen && (
          <TalentIndicatorSidebar
            onClose={() => setIsTalentIndicatorSidebarOpen(false)}
            canUpdate={canUpdate}
            assessmentType={AssessmentType.Manager}
            talentIndicatorData={applicableTalentIndicators}
            talentIndicatorSetup={
              upwardReviewStep?.talentIndicatorReviewConfig ?? {
                talentIndicators: [],
              }
            }
            onUpdateTalentIndicators={(talentIndicatorReviewConfig) => {
              onAddOrUpdateStep({
                ...upwardReviewStep,
                talentIndicatorReviewConfig,
              });
            }}
          />
        )}
        {isAssessmentGroupSidebarOpen && (
          <AssessmentGroupSidebar
            assessmentType={AssessmentType.Manager}
            assessmentGroupId={selectedAssessmentGroup?.id}
            assessmentGroupToDuplicateId={assessmentGroupToDuplicate?.id}
            onClose={() => {
              setAssessmentGroupToDuplicate(null);
              setIsAssessmentGroupSidebarOpen(false);
            }}
            onAssessmentGroupCreated={(assessmentGroup) => {
              onAssessmentGroupCreated(assessmentGroup);
              onAddOrUpdateStep({
                ...upwardReviewStep,
                questionSetIds: [assessmentGroup.id],
              });
            }}
            onAssessmentGroupArchived={(assessmentGroupId) =>
              onAssessmentGroupArchived(assessmentGroupId)
            }
          />
        )}
        <ComplianceProgramFormStep
          title={`Upward ${label("review", { capitalize: true })}`}
        >
          <div className="px-6 pb-6 flex flex-col gap-3">
            <div className="flex items-center gap-3 p-4 bg-white border rounded">
              <QuestionSetSelector
                assessmentType={AssessmentType.Manager}
                availableAssessmentGroupSelectOptions={
                  availableAssessmentGroupSelectOptions
                }
                canUpdate={canUpdate}
                onUpdateAssessmentTemplate={(assessmentGroupId) => {
                  onAddOrUpdateStep({
                    ...upwardReviewStep,
                    questionSetIds: assessmentGroupId
                      ? [assessmentGroupId]
                      : [],
                  });
                }}
                onDuplicateAssessmentGroup={setAssessmentGroupToDuplicate}
                onOpenAssessmentGroupSidebar={() =>
                  setIsAssessmentGroupSidebarOpen(true)
                }
                selectedAssessmentGroup={selectedAssessmentGroup}
              />
            </div>

            {waffle.flag_is_active("talent-indicators") && (
              <div className="flex items-center justify-between p-4 bg-white border rounded">
                <div className="flex items-center gap-2">
                  <div className="text-sm">Manager Talent Indicators</div>
                  <InfoTooltip text="Use this if you would like to assess managers on standard talent indicators." />
                </div>

                {applicableTalentIndicators.length === 0 ? (
                  <WarningTooltip
                    size={6}
                    text="There was a problem loading talent indicators"
                  />
                ) : (
                  <div className="flex items-center gap-4">
                    <Button
                      className="mb-1"
                      disabled={
                        !upwardReviewStep ||
                        !upwardReviewStep?.talentIndicatorReviewConfig ||
                        upwardReviewStep?.talentIndicatorReviewConfig
                          .talentIndicators?.length === 0
                      }
                      icon={TopicflowIconMap.edit}
                      theme={buttonTheme.iconGray}
                      onClick={() => setIsTalentIndicatorSidebarOpen(true)}
                    />
                    <ToggleSwitch
                      disabled={!canUpdate || !upwardReviewStep}
                      checked={
                        !!upwardReviewStep?.talentIndicatorReviewConfig &&
                        upwardReviewStep.talentIndicatorReviewConfig
                          .talentIndicators.length > 0
                      }
                      onChange={(enabled) => {
                        onAddOrUpdateStep({
                          ...upwardReviewStep,
                          talentIndicatorReviewConfig: {
                            ...upwardReviewStep.talentIndicatorReviewConfig,
                            talentIndicators: enabled
                              ? applicableTalentIndicators.map(
                                  (talentIndicator) => ({
                                    talentIndicatorId: talentIndicator.id,
                                    responses:
                                      AssessmentQuestionResponses.ExcludeSelfAssessment,
                                    responseVisibility:
                                      AssessmentQuestionResponseVisibility.HiddenFromSubject,
                                    comment: AssessmentQuestionComment.Optional,
                                    description: talentIndicator.description,
                                  }),
                                )
                              : [],
                          },
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </ComplianceProgramFormStep>
      </>
    )
  );
};
export default UpwardAssessmentStep;
